import Toast from "@moltech/molui/toast";

import { encryptUrlData } from "../encryption/CryptoService";
import { isNotificationUnread } from "./notificationDisplay";

/** Applicant details expects encrypted ids; SLA/reminder links may send plain numeric ids. */
const resolveInternalRedirectUrl = (redirectUrl) => {
  const trimmedUrl = redirectUrl.trim();
  const plainMatch = trimmedUrl.match(/^\/applicant-details\/(\d+)(\?.*)?$/i);
  if (!plainMatch) return trimmedUrl;
  return `/applicant-details/${encryptUrlData(Number(plainMatch[1]))}${plainMatch[2] || ""}`;
};

const INVALID_REDIRECT_MESSAGE = "This notification link is unavailable.";
const MARK_READ_ERROR_MESSAGE =
  "We couldn't update this notification. Please try again.";
const MARK_SELECTED_ERROR_MESSAGE =
  "We couldn't update one or more notifications. Please try again.";

export const isInternalNotificationRedirect = (redirectUrl) => {
  if (!redirectUrl || typeof redirectUrl !== "string") {
    return false;
  }

  const trimmedUrl = redirectUrl.trim();
  if (!trimmedUrl || trimmedUrl.startsWith("//") || trimmedUrl.includes("\\")) {
    return false;
  }

  if (/^(?:https?:)?\/\//i.test(trimmedUrl)) {
    return false;
  }

  if (/^(?:javascript|data|vbscript):/i.test(trimmedUrl)) {
    return false;
  }

  if (!trimmedUrl.startsWith("/")) {
    return false;
  }

  try {
    const parsedUrl = new URL(trimmedUrl, window.location.origin);
    return parsedUrl.origin === window.location.origin;
  } catch {
    return false;
  }
};

export const markNotificationAsReadSafely = async ({
  notification,
  markNotificationAsRead,
}) => {
  if (
    !notification?.notificationId ||
    !isNotificationUnread(notification) ||
    typeof markNotificationAsRead !== "function"
  ) {
    return { success: true };
  }

  try {
    await markNotificationAsRead({
      notificationId: notification.notificationId,
    }).unwrap();

    return { success: true };
  } catch {
    Toast.error(MARK_READ_ERROR_MESSAGE);
    return { success: false };
  }
};

export const markSelectedNotificationsAsRead = async ({
  notifications = [],
  notificationIds = [],
  markNotificationAsRead,
}) => {
  if (!notificationIds.length || typeof markNotificationAsRead !== "function") {
    return { success: true, updatedCount: 0 };
  }

  const unreadNotificationIds = notifications
    .filter(
      (notification) =>
        notificationIds.includes(notification?.notificationId) &&
        isNotificationUnread(notification),
    )
    .map((notification) => notification.notificationId);

  if (!unreadNotificationIds.length) {
    return { success: true, updatedCount: 0 };
  }

  try {
    for (const notificationId of unreadNotificationIds) {
      await markNotificationAsRead({ notificationId }).unwrap();
    }

    return { success: true, updatedCount: unreadNotificationIds.length };
  } catch {
    Toast.error(MARK_SELECTED_ERROR_MESSAGE);
    return { success: false, updatedCount: 0 };
  }
};

export const handleNotificationRedirect = async ({
  notification,
  navigate,
  markNotificationAsRead,
  onBeforeNavigate,
  onComplete,
}) => {
  if (!notification) {
    return { handled: false, reason: "missing-notification" };
  }

  const { success: markReadSucceeded } = await markNotificationAsReadSafely({
    notification,
    markNotificationAsRead,
  });

  if (!markReadSucceeded) {
    return { handled: false, reason: "mark-read-failed" };
  }

  const redirectUrl = notification?.redirectUrl?.trim();

  if (!redirectUrl) {
    onComplete?.("marked-read");
    return { handled: true, reason: "marked-read" };
  }

  if (!isInternalNotificationRedirect(redirectUrl)) {
    Toast.warn(INVALID_REDIRECT_MESSAGE);
    onComplete?.("invalid-redirect");
    return { handled: false, reason: "invalid-redirect" };
  }

  onBeforeNavigate?.();
  navigate(resolveInternalRedirectUrl(redirectUrl));
  onComplete?.("navigated");

  return { handled: true, reason: "navigated" };
};
